import { buildMentionDisplay, END_CHARS } from '@/utils/mention';
import type { StoryboardDialogueItem } from '@/hooks/api/useStoryboardDialogues';
import type { MentionResource } from '@/types/mention';
import type { MentionToken, ReferenceAssetKind, ReferenceImage } from '@/types/conversation-input';
import type { VisualStyle } from '@/types';
import {
  buildVisualStyleCommandMeta,
  CUSTOM_VISUAL_STYLE,
  resolveVisualStyleCommandDisplay,
} from '@/components/properties/conversations/visual-style-command';
import { buildReferenceAssetEntries } from '@/hooks/features/conversations/reference-asset-autocomplete';
import type { AIPromptCommandMenuItem } from '@/types/ai-prompt-command';

const CAMERA_CONTROL_DISPLAY = '/相机控制';
const ANGLE_CONTROL_DISPLAY = '/角度控制';
const LIGHT_CONTROL_DISPLAY = '/灯光控制';
const CHAR_THREE_VIEW_DISPLAY = '/角色形象图[三视图]';
const CHAR_REAL_PERSON_IMAGE_DISPLAY = '/角色形象图[真人版]';
const CHAR_EXPRESSION_IMAGE_DISPLAY = '/角色形象图[表情版]';
const PROP_IMAGE_DISPLAY = '/道具外观图';
const PROP_THREE_VIEW_DISPLAY = '/道具外观图[三视图]';
const VISUAL_STYLE_DISPLAY = '/视觉风格';
const BOARD_GRID_DISPLAY = '/分镜宫格镜头';
const BOARD_GRID_4_DISPLAY = '/分镜宫格镜头[4宫格]';
const BOARD_GRID_9_DISPLAY = '/分镜宫格镜头[9宫格]';
const BOARD_GRID_12_DISPLAY = '/分镜宫格镜头[12宫格]';
const BOARD_GRID_16_DISPLAY = '/分镜宫格镜头[16宫格]';
const BOARD_GRID_24_DISPLAY = '/分镜宫格镜头[24宫格]';
const CHARACTER_IMAGE_COMMAND_KEYS = new Set([
  'charThreeView',
  'charRealPersonImage',
  'charExpressionImage',
]);

function buildAIPromptCommandDisplay(label: string, contentLabel?: string): string {
  const normalizedLabel = label.replace(/^\/+/, '').trim();
  return contentLabel ? `/${normalizedLabel}[${contentLabel}]` : `/${normalizedLabel}`;
}

type AutoMentionCandidate =
  | {
      display: string;
      kind: 'control-camera';
    }
  | {
      display: string;
      kind: 'control-angle';
    }
  | {
      display: string;
      kind: 'control-light';
    }
  | {
      display: string;
      kind: 'command';
      commandKey: string;
      commandMeta?: MentionToken['commandMeta'];
    }
  | {
      display: string;
      kind: 'resource';
      resource: MentionResource;
    }
  | {
      display: string;
      kind: 'reference-image';
      referenceImageId: string;
      referenceImageIndex: number;
      referenceImageUrl?: string;
      referenceImageFileSize?: number;
      referenceImageKind?: ReferenceAssetKind;
      referenceImageMimeType?: string;
    }
  | {
      display: string;
      kind: 'dialogue';
      dialogueItems: MentionToken['dialogueItems'];
    };

interface ResolveAutoMentionTokensParams {
  text: string;
  existingTokens: MentionToken[];
  resources: MentionResource[];
  referenceImages: ReferenceImage[];
  dialogues: StoryboardDialogueItem[];
  controlMentionsEnabled: boolean;
  allowAngleControl: boolean;
  allowLightControl: boolean;
  /** 灯光控制最多只允许 1 个（自动识别时超出部分丢弃） */
  limitLightControlToOne?: boolean;
  /** 为 false（图片模式）时，相机控制和角度控制也限制为最多 1 个 */
  allowMultipleControlMentions?: boolean;
  /** 为 true 时在候选中加入角色形象图指令 */
  allowCharThreeView?: boolean;
  /** 为 true 时在候选中加入 /道具外观图[三视图] 指令 */
  allowPropThreeView?: boolean;
  /** 为 true 时在候选中加入 /视觉风格 指令 */
  allowVisualStyle?: boolean;
  /** 视觉风格列表，用于识别 /视觉风格[风格名] 文本 */
  visualStyles?: VisualStyle[];
  /** 当前作用域内的系统/组织/个人动态指令 */
  promptCommands?: AIPromptCommandMenuItem[];
  /** 为 true 时在候选中加入分镜宫格指令（boardGrid4/9/12） */
  allowStoryboardGrid?: boolean;
}

function buildDialogueMentionDisplay(dialogue: StoryboardDialogueItem): string {
  const name = dialogue.characterName?.trim() || '旁白';
  return `@${name}[${dialogue.content}]`;
}

function buildReferenceAssetMentionDisplay(
  index: number,
  kind: 'image' | 'video' | 'audio'
): string {
  if (kind === 'video') return `@参考视频[视频${index}]`;
  if (kind === 'audio') return `@参考音频[音频${index}]`;
  return `@参考图[图${index}]`;
}

function isBoundaryChar(char: string | undefined): boolean {
  return !char || END_CHARS.includes(char) || /[\])}"'》】）”’]/.test(char);
}

function isStartBoundary(char: string | undefined): boolean {
  return !char || /\s/.test(char) || /[([{'"“‘《【（，。！？：；,.!?:;]/.test(char);
}

function buildAutoMentionCandidates(params: {
  resources: MentionResource[];
  referenceImages: ReferenceImage[];
  dialogues: StoryboardDialogueItem[];
  controlMentionsEnabled: boolean;
  allowAngleControl: boolean;
  allowLightControl: boolean;
  allowCharThreeView?: boolean;
  allowPropThreeView?: boolean;
  allowVisualStyle?: boolean;
  visualStyles?: VisualStyle[];
  promptCommands?: AIPromptCommandMenuItem[];
  allowStoryboardGrid?: boolean;
}): AutoMentionCandidate[] {
  const candidates: AutoMentionCandidate[] = [];

  candidates.push(...buildUserAIPromptCommandCandidates(params.promptCommands ?? []));

  if (params.controlMentionsEnabled) {
    candidates.push({ display: CAMERA_CONTROL_DISPLAY, kind: 'control-camera' });
    if (params.allowAngleControl) {
      candidates.push({ display: ANGLE_CONTROL_DISPLAY, kind: 'control-angle' });
    }
    if (params.allowLightControl) {
      candidates.push({ display: LIGHT_CONTROL_DISPLAY, kind: 'control-light' });
    }
  }

  if (params.allowCharThreeView) {
    candidates.push({
      display: CHAR_THREE_VIEW_DISPLAY,
      kind: 'command',
      commandKey: 'charThreeView',
    });
    candidates.push({
      display: CHAR_REAL_PERSON_IMAGE_DISPLAY,
      kind: 'command',
      commandKey: 'charRealPersonImage',
    });
    candidates.push({
      display: CHAR_EXPRESSION_IMAGE_DISPLAY,
      kind: 'command',
      commandKey: 'charExpressionImage',
    });
  }
  if (params.allowPropThreeView) {
    candidates.push({
      display: PROP_THREE_VIEW_DISPLAY,
      kind: 'command',
      commandKey: 'propThreeView',
    });
  }
  if (params.allowVisualStyle) {
    candidates.push({
      display: VISUAL_STYLE_DISPLAY,
      kind: 'command',
      commandKey: 'visualStyle',
    });
    candidates.push({
      display: resolveVisualStyleCommandDisplay(CUSTOM_VISUAL_STYLE),
      kind: 'command',
      commandKey: 'visualStyle',
      commandMeta: buildVisualStyleCommandMeta(CUSTOM_VISUAL_STYLE),
    });
    params.visualStyles?.forEach((style) => {
      candidates.push({
        display: resolveVisualStyleCommandDisplay(style),
        kind: 'command',
        commandKey: 'visualStyle',
        commandMeta: buildVisualStyleCommandMeta(style),
      });
    });
  }
  if (params.allowStoryboardGrid) {
    // 分镜宫格系列：用户可切换任意格数，所有变体都需要加入候选以保证重新应用时能识别
    candidates.push({ display: BOARD_GRID_4_DISPLAY, kind: 'command', commandKey: 'boardGrid4' });
    candidates.push({ display: BOARD_GRID_9_DISPLAY, kind: 'command', commandKey: 'boardGrid9' });
    candidates.push({
      display: BOARD_GRID_12_DISPLAY,
      kind: 'command',
      commandKey: 'boardGrid12',
    });
    candidates.push({
      display: BOARD_GRID_16_DISPLAY,
      kind: 'command',
      commandKey: 'boardGrid16',
    });
    candidates.push({
      display: BOARD_GRID_24_DISPLAY,
      kind: 'command',
      commandKey: 'boardGrid24',
    });
  }

  params.dialogues.forEach((dialogue) => {
    candidates.push({
      display: buildDialogueMentionDisplay(dialogue),
      kind: 'dialogue',
      dialogueItems: [
        {
          characterName: dialogue.characterName?.trim() || '旁白',
          content: dialogue.content,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          emotion: dialogue.emotion ?? undefined,
          sequenceOrder: dialogue.sequenceOrder,
        },
      ],
    });
  });

  params.resources.forEach((resource) => {
    candidates.push({
      display: buildMentionDisplay(resource),
      kind: 'resource',
      resource,
    });
  });

  buildReferenceAssetEntries(params.referenceImages).forEach((entry) => {
    candidates.push({
      display: buildReferenceAssetMentionDisplay(entry.referenceImageIndex, entry.kind),
      kind: 'reference-image',
      referenceImageId: entry.image.id,
      referenceImageIndex: entry.referenceImageIndex,
      referenceImageUrl: entry.image.remoteUrl ?? entry.image.url,
      referenceImageFileSize: entry.image.fileSize,
      referenceImageKind: entry.kind,
      referenceImageMimeType: entry.referenceImageMimeType,
    });
  });

  return candidates;
}

function buildExistingTokenKey(token: MentionToken): string {
  return `${token.start}:${token.end}:${token.display}:${token.kind}`;
}

function createTokenFromCandidate(
  candidate: AutoMentionCandidate,
  start: number,
  end: number
): MentionToken {
  return {
    id: `${candidate.kind}:${start}:${end}:${candidate.display}`,
    start,
    end,
    display: candidate.display,
    kind: candidate.kind,
    resource: candidate.kind === 'resource' ? candidate.resource : undefined,
    referenceImageId: candidate.kind === 'reference-image' ? candidate.referenceImageId : undefined,
    referenceImageIndex:
      candidate.kind === 'reference-image' ? candidate.referenceImageIndex : undefined,
    referenceImageUrl:
      candidate.kind === 'reference-image' ? candidate.referenceImageUrl : undefined,
    referenceImageFileSize:
      candidate.kind === 'reference-image' ? candidate.referenceImageFileSize : undefined,
    referenceImageKind:
      candidate.kind === 'reference-image' ? candidate.referenceImageKind : undefined,
    referenceImageMimeType:
      candidate.kind === 'reference-image' ? candidate.referenceImageMimeType : undefined,
    dialogueItems: candidate.kind === 'dialogue' ? candidate.dialogueItems : undefined,
    commandKey: candidate.kind === 'command' ? candidate.commandKey : undefined,
    commandMeta: candidate.kind === 'command' ? candidate.commandMeta : undefined,
    // 从文本自动识别的控制提及需要默认 controlPayload，否则弹层打开时参数为空
    controlPayload:
      candidate.kind === 'control-camera'
        ? { zoom: 5, aperture: 2.8 }
        : candidate.kind === 'control-angle'
          ? { horizontal: 30, vertical: 20, distance: 5, orientationOffset: 0 }
          : candidate.kind === 'control-light'
            ? { light: { horizontal: 45, vertical: 45, intensity: 50 } }
            : undefined,
  };
}

function compareCandidates(a: AutoMentionCandidate, b: AutoMentionCandidate): number {
  const priority = (candidate: AutoMentionCandidate) => {
    switch (candidate.kind) {
      case 'control-camera':
      case 'control-angle':
      case 'control-light':
      case 'command':
        return 0;
      case 'dialogue':
        return 1;
      case 'resource':
        return 2;
      case 'reference-image':
        return 3;
    }
  };

  const priorityDiff = priority(a) - priority(b);
  if (priorityDiff !== 0) return priorityDiff;
  return b.display.length - a.display.length;
}

function isCharacterImageCommandKey(commandKey: string | undefined): boolean {
  return Boolean(commandKey && CHARACTER_IMAGE_COMMAND_KEYS.has(commandKey));
}

export function normalizeLegacyCommandDisplays(text: string): string {
  return text
    .replace(/\/角色形象三视图/g, CHAR_THREE_VIEW_DISPLAY)
    .replace(/\/角色形象图\[真人\]/g, CHAR_REAL_PERSON_IMAGE_DISPLAY)
    .replace(/\/道具外观图(?!\[)/g, PROP_THREE_VIEW_DISPLAY)
    .replace(/\/分镜宫格镜头(?!\[)/g, BOARD_GRID_9_DISPLAY);
}

function dedupeCharacterImageCommands(tokens: MentionToken[]): MentionToken[] {
  let hasCharacterImageCommand = false;
  return tokens.filter((token) => {
    if (token.kind !== 'command' || !isCharacterImageCommandKey(token.commandKey)) {
      return true;
    }
    if (hasCharacterImageCommand) {
      return false;
    }
    hasCharacterImageCommand = true;
    return true;
  });
}

export function resolveAutoMentionTokens({
  text,
  existingTokens,
  resources,
  referenceImages,
  dialogues,
  controlMentionsEnabled,
  allowAngleControl,
  allowLightControl,
  limitLightControlToOne = false,
  allowMultipleControlMentions = true,
  allowCharThreeView = false,
  allowPropThreeView = false,
  allowVisualStyle = false,
  visualStyles = [],
  promptCommands = [],
  allowStoryboardGrid = false,
}: ResolveAutoMentionTokensParams): MentionToken[] {
  if (!text) return [];

  const sortedCandidates = buildAutoMentionCandidates({
    resources,
    referenceImages,
    dialogues,
    controlMentionsEnabled,
    allowAngleControl,
    allowLightControl,
    allowCharThreeView,
    allowPropThreeView,
    allowVisualStyle,
    visualStyles,
    promptCommands,
    allowStoryboardGrid,
  }).sort(compareCandidates);

  const existingTokenMap = new Map(
    existingTokens.map((token) => [buildExistingTokenKey(token), token] as const)
  );
  const resolved: MentionToken[] = [];
  const occupiedRanges: Array<{ start: number; end: number }> = [];

  const isOverlapped = (start: number, end: number) =>
    occupiedRanges.some((range) => start < range.end && end > range.start);

  // 已存在的 MentionNode 代表用户显式插入的结构化节点。
  // 只要其 display 仍与当前文本对应，就应优先保留，不能再被自动识别边界规则反向删除。
  const preservedExistingTokens = [...existingTokens]
    .sort((a, b) => a.start - b.start)
    .filter((token) => {
      if (token.start < 0 || token.end > text.length || token.start >= token.end) {
        return false;
      }
      if (text.slice(token.start, token.end) !== token.display) {
        return false;
      }
      if (isOverlapped(token.start, token.end)) {
        return false;
      }
      occupiedRanges.push({ start: token.start, end: token.end });
      return true;
    });

  resolved.push(...preservedExistingTokens);

  sortedCandidates.forEach((candidate) => {
    let searchFrom = 0;
    while (searchFrom < text.length) {
      const start = text.indexOf(candidate.display, searchFrom);
      if (start === -1) break;
      const end = start + candidate.display.length;
      const previousChar = start > 0 ? text[start - 1] : undefined;
      const nextChar = end < text.length ? text[end] : undefined;

      // @name[tag] 和 /命令[选项] 自带 ] 作为尾部分隔符，允许后面紧跟中文正文；
      // 但仍要求前方是起始边界，避免把正文中紧贴前文的片段误识别为 mention。
      const isBracketedEndMention =
        candidate.display.startsWith('@') && candidate.display.endsWith(']');
      const isBracketedSlashCommand =
        candidate.kind === 'command' &&
        candidate.display.startsWith('/') &&
        candidate.display.endsWith(']');
      if (
        !isStartBoundary(previousChar) ||
        (!isBracketedEndMention && !isBracketedSlashCommand && !isBoundaryChar(nextChar)) ||
        isOverlapped(start, end)
      ) {
        searchFrom = start + 1;
        continue;
      }

      const key = `${start}:${end}:${candidate.display}:${candidate.kind}`;
      resolved.push(existingTokenMap.get(key) ?? createTokenFromCandidate(candidate, start, end));
      occupiedRanges.push({ start, end });
      searchFrom = end;
    }
  });

  const sorted = resolved.sort((a, b) => a.start - b.start);

  // 各控制类型的单实例限制：保留文本中出现最早的那个
  const needsLimit = limitLightControlToOne || !allowMultipleControlMentions;
  if (needsLimit) {
    const seen = new Set<string>();
    return dedupeCharacterImageCommands(
      sorted.filter((token) => {
        const limitCamera = !allowMultipleControlMentions && token.kind === 'control-camera';
        const limitAngle = !allowMultipleControlMentions && token.kind === 'control-angle';
        const limitLight = limitLightControlToOne && token.kind === 'control-light';
        // command 类型：按 commandKey 限制唯一性
        const limitCommandKey =
          token.kind === 'command' && token.commandKey ? `command:${token.commandKey}` : null;
        if (!limitCamera && !limitAngle && !limitLight && !limitCommandKey) return true;
        const dedupeKey = limitCommandKey ?? token.kind;
        if (seen.has(dedupeKey)) return false;
        seen.add(dedupeKey);
        return true;
      })
    );
  }

  return dedupeCharacterImageCommands(sorted);
}

export const CONTROL_MENTION_DISPLAYS = {
  camera: CAMERA_CONTROL_DISPLAY,
  angle: ANGLE_CONTROL_DISPLAY,
  light: LIGHT_CONTROL_DISPLAY,
} as const;

/** 指令集 commandKey → 编辑器占位符映射（与后端 command_set_prompt.py 保持一致） */
export const COMMAND_DISPLAYS: Record<string, string> = {
  characterImage: '/角色形象图',
  charThreeView: CHAR_THREE_VIEW_DISPLAY,
  charRealPersonImage: CHAR_REAL_PERSON_IMAGE_DISPLAY,
  charExpressionImage: CHAR_EXPRESSION_IMAGE_DISPLAY,
  propImage: PROP_IMAGE_DISPLAY,
  propThreeView: PROP_THREE_VIEW_DISPLAY,
  visualStyle: VISUAL_STYLE_DISPLAY,
  boardGrid: BOARD_GRID_DISPLAY,
  boardGrid4: BOARD_GRID_4_DISPLAY,
  boardGrid9: BOARD_GRID_9_DISPLAY,
  boardGrid12: BOARD_GRID_12_DISPLAY,
  boardGrid16: BOARD_GRID_16_DISPLAY,
  boardGrid24: BOARD_GRID_24_DISPLAY,
};

export function buildUserAIPromptCommandCandidates(
  userAIPromptCommands: AIPromptCommandMenuItem[]
): AutoMentionCandidate[] {
  return userAIPromptCommands.flatMap<AutoMentionCandidate>((cmd): AutoMentionCandidate[] => {
    if (cmd.promptContent.length > 0) {
      return cmd.promptContent.map((item) => ({
        display: buildAIPromptCommandDisplay(cmd.label, item.label),
        kind: 'command' as const,
        commandKey: item.commandKey ?? cmd.promptId,
        commandMeta: {
          promptId: cmd.promptId,
          promptName: cmd.name,
          promptLabel: cmd.label,
          commandKey: item.commandKey,
          outputType: cmd.outputType,
          usageScopes: cmd.usageScopes,
          promptContent: cmd.promptContent,
          description: cmd.description ?? null,
          selectedContentLabel: item.label,
          resolvedPromptSnapshot: item.content.trim(),
        },
      }));
    }

    return [
      {
        display: buildAIPromptCommandDisplay(cmd.label),
        kind: 'command' as const,
        commandKey: cmd.promptId,
        commandMeta: {
          promptId: cmd.promptId,
          promptName: cmd.name,
          promptLabel: cmd.label,
          commandKey: undefined,
          outputType: cmd.outputType,
          usageScopes: cmd.usageScopes,
          promptContent: cmd.promptContent,
          description: cmd.description ?? null,
          selectedContentLabel: undefined,
          resolvedPromptSnapshot: '',
        },
      },
    ];
  });
}
